/**
 * Keyboard navigation for the triage column (PRD docs/prd-keyboard.md §5).
 * Input-trap guard, `event.code` binding (AZERTY/QWERTZ/Dvorak-safe),
 * `E` dismiss, `J`/`K` next/prev, `Esc` release.
 */

import { useCallback, useEffect, useRef, useState } from "react";

/**
 * PRD §5.2 — suppress single-letter shortcuts inside text-entry
 * targets and during IME composition. Modifier-chord callers (leader)
 * skip this guard.
 */
export function shouldFireShortcut(e: KeyboardEvent): boolean {
  const target = e.target as HTMLElement | null;
  // Null target (synthetic events dispatched on the document root
  // with no specific target) → not in a text field, allow.
  if (!target) return true;
  if (target.tagName === "INPUT") return false;
  if (target.tagName === "TEXTAREA") return false;
  // `<select>` handles Esc / arrows / type-ahead itself — keydown still bubbles.
  // Deliberate extension to PRD §5.2 (which lists INPUT/TEXTAREA/contentEditable/role=textbox/IME but not SELECT).
  if (target.tagName === "SELECT") return false;
  if (target.isContentEditable) return false;
  if (typeof target.getAttribute === "function") {
    // Attribute fallback because jsdom doesn't compute `isContentEditable`.
    const ce = target.getAttribute("contenteditable");
    if (ce != null && ce.toLowerCase() !== "false") return false;
    if (target.getAttribute("role") === "textbox") return false;
  }
  // `code === "Process"` is the modern Chromium/Gecko replacement for `keyCode === 229`.
  if (e.isComposing || e.code === "Process") return false;
  return true;
}

export interface UseKeyboardNavigationOptions {
  /** Ordered selectable row ids (host's current render order). */
  ids: string[];
  /** Fired on `E`. Host owns the dismiss side effect. */
  onDismiss: (id: string) => void;
  /**
   * Whether to attach the document keydown listener. Required so
   * consumers make an explicit choice — defaulting to `true` would
   * silently install a global listener for callers who pass only
   * `ids` + `onDismiss`. Pass `false` to keep the hook's state
   * shape but skip the listener (e.g. for storybook fixtures, or
   * when a host owns its own listener wiring).
   */
  enabled: boolean;
  /**
   * Column-root ref. Events whose target is outside `rootRef.current`
   * are ignored — PRD §5.4 / §6.2 column-scope rule. The listener
   * itself stays on `document` so it isn't affected by ref-mount
   * timing; `rootRef.current` is read lazily at event time.
   *
   * Required — no default — so production callers can't forget the
   * scope. Pass `undefined` explicitly to opt into the document-wide
   * fallback (useful for tests that don't render a real column root).
   */
  rootRef: { current: HTMLElement | null } | undefined;
  /** Fired on `Esc`. Receives previous id; selection clears regardless. */
  onEscape?: (previousId: string | null) => void;
}

export interface UseKeyboardNavigationResult {
  selectedId: string | null;
  /** Select ids[0]. No-op if list is empty. */
  selectFirst: () => void;
  /** Select a specific id. Returns false if not in `ids` so the caller can fall through. */
  selectId: (id: string) => boolean;
}

/**
 * PRD §5 — column-local keyboard handler.
 *
 * Owns selection state (with same-position repair on id removal, so
 * `E` advances to the row that took the dismissed row's place) and
 * the document-level keydown listener. Does NOT own: the leader chord
 * (host one-shot), scrolling (host knows the scroll container), or
 * the dismiss network call (host wires `onDismiss`).
 */
export function useKeyboardNavigation(
  options: UseKeyboardNavigationOptions
): UseKeyboardNavigationResult {
  const { ids, onDismiss, onEscape, enabled, rootRef } = options;
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Selection repair: if `ids` shrinks and the selected id is gone,
  // pick the same-position neighbour (or the last id). PRD §5.1.
  const prevIdsRef = useRef<string[]>(ids);
  useEffect(() => {
    if (selectedId == null) {
      prevIdsRef.current = ids;
      return;
    }
    // Fast path: J/K re-runs hit this branch — closure read of
    // `selectedId` forces it into deps but the side-effect only fires
    // when the id has left `ids`.
    if (ids.includes(selectedId)) {
      prevIdsRef.current = ids;
      return;
    }
    const prevIdx = prevIdsRef.current.indexOf(selectedId);
    if (prevIdx === -1 || ids.length === 0) {
      setSelectedId(null);
    } else {
      const next = ids[Math.min(prevIdx, ids.length - 1)];
      setSelectedId(next ?? null);
    }
    prevIdsRef.current = ids;
  }, [ids, selectedId]);

  // Ref-mirror handler-captured inputs so the keydown listener doesn't re-attach per render.
  // The assignment lives in useEffect (not the render body) so it only runs for committed
  // renders — React 18 concurrent rendering can render-then-discard, and a render-body write
  // would leave the ref holding a value from a render that never committed (see #423).
  const idsRef = useRef(ids);
  const onDismissRef = useRef(onDismiss);
  const onEscapeRef = useRef(onEscape);
  const selectedIdRef = useRef(selectedId);
  const rootRefRef = useRef(rootRef);
  useEffect(() => {
    idsRef.current = ids;
    onDismissRef.current = onDismiss;
    onEscapeRef.current = onEscape;
    selectedIdRef.current = selectedId;
    rootRefRef.current = rootRef;
  });

  const selectFirst = useCallback(() => {
    const first = idsRef.current[0];
    setSelectedId(first ?? null);
  }, []);

  const selectId = useCallback((id: string): boolean => {
    if (!idsRef.current.includes(id)) return false;
    setSelectedId(id);
    return true;
  }, []);

  useEffect(() => {
    if (!enabled) return;
    // Dev-only signal: if a caller wires up keyboard nav without a
    // scope, every focused control on the page receives E/J/K. The
    // type permits `undefined` (tests use the unscoped path) so this
    // is the strongest "you probably meant to pass rootRef" we can
    // give. Suppressed in production. Read NODE_ENV via globalThis so
    // this stays type-clean in browser-only consumers without pulling
    // in `@types/node`.
    const env = (globalThis as { process?: { env?: { NODE_ENV?: string } } }).process?.env
      ?.NODE_ENV;
    if (env === "development" && !rootRefRef.current) {
      console.warn(
        "[useKeyboardNavigation] enabled without rootRef — listener will fire for keys anywhere in the document. Pass `rootRef` to scope to a column."
      );
    }
    // Document-level listener (PRD §2.1: keys only land when the
    // surface document has focus). Scoped by `rootRef.contains(target)`
    // so unrelated controls (header gear, etc.) don't fire E/J/K.
    const handler = (ke: KeyboardEvent) => {
      const rootRefHandle = rootRefRef.current;
      const root = rootRefHandle?.current;
      const evtTarget = ke.target as Node | null;
      // Fail closed when the host *provided* a rootRef but its
      // `.current` hasn't populated yet (sub-frame window before
      // the column DOM mounts). Without this, every keydown for
      // that brief window would slip through the scope check.
      if (rootRefHandle && !root) return;
      // Null `evtTarget` (synthetic events dispatched on `document` itself, as
      // some tests do) passes the scope check by design — mirrors
      // `shouldFireShortcut`'s null-target allowance.
      if (root && evtTarget && !root.contains(evtTarget)) return;

      // PRD §5.3 explicitly uses `event.key === "Escape"`: Esc's
      // keyboard position varies; the symbolic meaning is what
      // matters. (E/J/K bind on `event.code` for layout safety.)
      if (ke.key === "Escape") {
        // Deliberate deviation from PRD §5.2 ("Esc doesn't need the
        // input-trap guard"): Esc inside a search box must cancel the
        // search, not clear triage selection — the input owns Esc.
        if (!shouldFireShortcut(ke)) return;
        const prev = selectedIdRef.current;
        // Always preventDefault + fire onEscape so the host can
        // transition out of the keyboard-mode state machine even
        // when the column root holds focus with no row selected
        // (PRD §6.2 COLUMN_FOCUSED_NO_SELECT → COLUMN_VISIBLE_NO_FOCUS).
        // Selection-clear only fires when something was selected.
        ke.preventDefault();
        if (prev != null) setSelectedId(null);
        onEscapeRef.current?.(prev);
        return;
      }

      if (!shouldFireShortcut(ke)) return;
      // Skip while any modifier is held — modifier combos belong to leader / browser / OS.
      // Shift included so Shift+J/Shift+K (vim-muscle-memory) doesn't trip nav.
      if (ke.ctrlKey || ke.metaKey || ke.altKey || ke.shiftKey) return;

      const list = idsRef.current;
      const current = selectedIdRef.current;
      const idx = current != null ? list.indexOf(current) : -1;

      switch (ke.code) {
        case "KeyJ": {
          if (list.length === 0) return;
          ke.preventDefault();
          const nextIdx = idx === -1 ? 0 : Math.min(idx + 1, list.length - 1);
          setSelectedId(list[nextIdx] ?? null);
          break;
        }
        case "KeyK": {
          if (list.length === 0) return;
          ke.preventDefault();
          const nextIdx = idx === -1 ? 0 : Math.max(idx - 1, 0);
          setSelectedId(list[nextIdx] ?? null);
          break;
        }
        case "KeyE": {
          if (current == null) return;
          // Block key-repeat: holding `E` would chain-dismiss every
          // notification under the held key. J/K above are
          // deliberately repeat-safe (fast nav is desirable);
          // dismiss is destructive and must be one-press-one-action.
          if (ke.repeat) return;
          ke.preventDefault();
          onDismissRef.current(current);
          // Advance happens in the repair effect once host removes id.
          break;
        }
      }
    };
    // The `addEventListener("keydown", ...)` overload accepts a
    // `(e: KeyboardEvent) => void` directly — no cast needed.
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [enabled]);

  return { selectedId, selectFirst, selectId };
}
